import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { graphql, withApollo } from 'react-apollo';
import { mapProps } from 'recompose';
import { connect } from 'react-redux';

import CheckoutCartItem from '../CheckoutCartItem';
import CheckoutCartAddress from './CheckoutCartAddress';
import PriceBreakdownList from '../PriceBreakdownList';
import CheckoutCartPaymentPanel from './CheckoutCartPaymentPanel';
import getAccount from '../../../../apps/temp/graphql/queries/getAccount';
import {
  getCheckoutCart,
  getCheckoutCartCheckoutItems,
  getCheckoutCartTotalItems,
  getCheckoutCartTotalPrice,
  getCheckoutPreviousCart,
  checkoutCartRequiresAddress,
  getUnconfiguredAlbumPrintPackageCartItems,
  getUnconfiguredPurchasedPrintPackageCartItems,
  getUnconfiguredTaggedAlbumPrintPackageCartItems,
} from '../../redux/checkoutSelectors';
import {
  clearCart as clearCartAction,
  onCartItemRemove as onCartItemRemoveAction,
} from '../../redux/checkoutActions';
import { openAlert as openAlertAction } from '../../../../actions/modal-actions';
import { getCheckoutPriceQuery } from '../../../../apps/temp/graphql/queries/getCheckoutPrice';
import { checkout as checkoutMutation } from '../../../../graphql/mutations/checkout';
import CART_ITEM from '../../constants/cartItems';
import { CURRENCY } from '../../../../constants/currency';
import useCheckoutCart from './hooks/useCheckoutCart';
import { getErrorString } from '../../../../util/normalize-errors';
import PhoneAuthWithEmailFallback from '../../../../components/auth/PhoneAuthWithEmailFallback';
import { trackAuthEvent } from '../../../../apps/web-gallery/utilities/analytics';
import Spinner from '../../../../components/global/Spinner';
import CheckoutCartPendingPurchases from './CheckoutCartPendingPurchases';

const propTypes = {
  className: PropTypes.string,
  account: PropTypes.shape({
    id: PropTypes.string,
    addresses: PropTypes.array,
  }),
  cart: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(Object.values(CART_ITEM)),
      albumFolder: PropTypes.object,
      album: PropTypes.object,
      albumCustomPrint: PropTypes.object,
      shipping: PropTypes.bool,
    }),
  ).isRequired,
  checkoutItems: PropTypes.object,
  totalCartItems: PropTypes.number,
  computedPrice: PropTypes.shape({
    value: PropTypes.number,
    currency: PropTypes.oneOf(Object.values(CURRENCY)),
  }),
  checkout: PropTypes.func.isRequired,
  openAlert: PropTypes.func.isRequired,
  pricing: PropTypes.shape({
    total_price: PropTypes.object,
    total_shipping_cost: PropTypes.object,
    total_tax: PropTypes.object,
  }),
  loadingPricing: PropTypes.bool,
  errorFetchingPricing: PropTypes.object,
  refetchPricing: PropTypes.func,
  requiresAddress: PropTypes.bool,
  itemsPriceBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      checkout_item_client_id: PropTypes.string,
      price: PropTypes.object,
      discount_price: PropTypes.object,
    }),
  ),
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  clearCart: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  checkoutRequirements: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string.isRequired,
      condition: PropTypes.bool.isRequired,
      scrollElement: PropTypes.string,
    }).isRequired,
  ),
  onClose: PropTypes.func,
  client: PropTypes.shape({
    resetStore: PropTypes.func,
  }),
  authenticated: PropTypes.bool,
  unconfiguredAlbumPrintPackageCartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  unconfiguredPurchasedPrintPackageCartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  unconfiguredTaggedAlbumPrintPackageCartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  stripeModalProps: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  }),
};

const CheckoutCart = ({
  className,
  cart,
  checkoutItems,
  totalCartItems,
  account,
  computedPrice,
  checkout,
  openAlert,
  pricing,
  loadingPricing,
  refetchPricing,
  errorFetchingPricing,
  requiresAddress,
  location,
  itemsPriceBreakdown = [],
  clearCart,
  onSuccess,
  checkoutRequirements = [],
  onClose,
  client,
  authenticated,
  unconfiguredAlbumPrintPackageCartItems,
  unconfiguredPurchasedPrintPackageCartItems,
  unconfiguredTaggedAlbumPrintPackageCartItems,
  stripeModalProps,
}) => {
  const classes = classNames({
    [className]: !!className,
    'checkout-cart': true,
  });

  const { onCheckout, allowedPromoCodes, submitting } = useCheckoutCart({
    mutation: checkout,
    onSuccess: () => {
      // We clear the cart on success, as avoiding to do so will mean checkoutPrice will be recalled with
      // these items which could fail if we have Photo Download types, since these are blocked from re-purchase.
      clearCart();
      onSuccess?.({ cart });
    },
    onError: error => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
    openAlert,
    pricing,
    cart,
    checkoutItems,
    loadingPricing,
    refetchPricing,
    errorFetchingPricing,
  });

  useEffect(() => {
    if (totalCartItems === 0) {
      onClose?.();
    }
  }, [totalCartItems]);

  if (!authenticated) {
    return (
      <div className="p-4">
        <PhoneAuthWithEmailFallback
          introText={
            'In order to complete a purchase we need your phone number. ' +
            'Waldo will text a verification code to the number below.'
          }
          onAuthSuccess={authType => {
            client.resetStore();
            trackAuthEvent(authType);
          }}
          defaultEmailStep="LOGIN"
        />
      </div>
    );
  }

  if (!account) {
    return (
      <div
        data-testid="loading-spinner"
        className="w-100 d-flex align-items-center justify-content-center p-4"
      >
        <Spinner />
      </div>
    );
  }

  const [address] = account?.addresses ?? [];

  const requirements = [
    ...checkoutRequirements,
    {
      message: 'Please add a shipping address.',
      condition: !(requiresAddress && !address),
      scrollElement: 'checkout-shipping-address',
    },
    {
      message: 'Some items in your cart require you to complete configuration.',
      condition:
        (unconfiguredAlbumPrintPackageCartItems?.length ?? 0) === 0 &&
        (unconfiguredPurchasedPrintPackageCartItems?.length ?? 0) === 0 &&
        (unconfiguredTaggedAlbumPrintPackageCartItems?.length ?? 0) === 0,
    },
  ];

  const totalComputedPrice =
    computedPrice.value +
    (pricing?.total_shipping_cost.value ?? 0) +
    (pricing?.total_tax.value ?? 0);

  return (
    <Fragment>
      <CheckoutCartPendingPurchases location={location} />
      <div className={classes}>
        <div className="d-flex align-items-center mb-2">
          <h3 className="m-0 text-extra-bold text-faded">Order Summary</h3>

          <span className="text-sm ml-auto text-faded text-bold">
            {totalCartItems} {pluralize('item', totalCartItems)}
          </span>
        </div>

        <div className="mb-4">
          {cart.map(item => {
            const price = itemsPriceBreakdown.find(
              priceBreakdown =>
                priceBreakdown.checkout_item_client_id === item.id,
            );

            return (
              <CheckoutCartItem
                key={item.id}
                item={item}
                loading={loadingPricing}
                pricing={price}
              />
            );
          })}
        </div>

        {requiresAddress && (
          <CheckoutCartAddress
            account={account}
            checkoutItems={checkoutItems}
            addressRequired
          />
        )}

        {cart.length > 0 && (
          <Fragment>
            {pricing?.total_price.value > 0 && (
              <Fragment>
                <h3 className="text-extra-bold text-faded">Total</h3>
                <div className="rounded-box bg-gray-lightest">
                  <PriceBreakdownList
                    pricing={pricing}
                    loading={loadingPricing}
                  />
                </div>
              </Fragment>
            )}

            {cart.length > 0 && (
              <CheckoutCartPaymentPanel
                account={account}
                location={location}
                loading={loadingPricing}
                finalPrice={pricing?.total_price.value}
                totalPrice={totalComputedPrice}
                onStripeToken={onCheckout}
                submitting={submitting}
                blockers={requirements.filter(
                  requirement => !requirement.condition,
                )}
                checkoutItems={checkoutItems}
                allowedPromoCodes={allowedPromoCodes}
                stripeModalProps={stripeModalProps}
              />
            )}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

CheckoutCart.propTypes = propTypes;

const mapStateToProps = state => ({
  cart: getCheckoutCart(state),
  previousCart: getCheckoutPreviousCart(state),
  totalCartItems: getCheckoutCartTotalItems(state),
  checkoutItems: getCheckoutCartCheckoutItems(state),
  computedPrice: getCheckoutCartTotalPrice(state),
  requiresAddress: checkoutCartRequiresAddress(state),
  authenticated: state.auth.authenticated,
  unconfiguredAlbumPrintPackageCartItems: getUnconfiguredAlbumPrintPackageCartItems(
    state,
  ),
  unconfiguredPurchasedPrintPackageCartItems: getUnconfiguredPurchasedPrintPackageCartItems(
    state,
  ),
  unconfiguredTaggedAlbumPrintPackageCartItems: getUnconfiguredTaggedAlbumPrintPackageCartItems(
    state,
  ),
});

const mapDispatchToProps = {
  onCartItemRemove: onCartItemRemoveAction,
  clearCart: clearCartAction,
  openAlert: openAlertAction,
};

const getCheckoutPrice = graphql(getCheckoutPriceQuery, {
  options: ({ account, checkoutItems }) => {
    const [address] = account.addresses;

    return {
      variables: {
        account_id: account.id,
        address_id: address?.id,
        ...checkoutItems,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    };
  },
  skip: ({ account, cart }) => !account || !cart.length,
});

const withCheckoutMutation = graphql(checkoutMutation, {
  props: ({ mutate, ownProps: { account, checkoutItems } }) => ({
    checkout: ({ stripeTokenId, email, promoCode }) =>
      mutate({
        variables: {
          input: {
            account_id: account.id,
            account_address_id: account.addresses?.[0]?.id,
            stripe_token: stripeTokenId,
            email_address: email,
            promo_code: promoCode,
            ...checkoutItems,
          },
        },
      }),
  }),
});

export { CheckoutCart as CheckoutCartComponent };

export default compose(
  withRouter,
  withApollo,
  connect(mapStateToProps, mapDispatchToProps),
  getAccount,
  mapProps(({ data, ...props }) => ({
    account: data?.me,
    ...props,
  })),
  getCheckoutPrice,
  mapProps(({ data, ...props }) => ({
    pricing: data?.checkoutPrice,
    loadingPricing: data?.loading,
    refetchPricing: data?.refetch,
    errorFetchingPricing: data?.error,
    itemsPriceBreakdown: [
      ...(data?.checkoutPrice?.items_price_breakdown?.photo_items ?? []),
    ],
    ...props,
  })),
  withCheckoutMutation,
)(CheckoutCart);
